import { api } from './authService';

// 获取当前租户的API基础路径
function getApiBase(): string {
  const tenantCode = localStorage.getItem('currentTenant') || 'default';
  return `/${tenantCode}`;
}

export interface Service {
  id: number;
  tenant_code: string;
  service_name: string;
  service_url: string;
  auth_type: string;
  username?: string;
  password?: string;
  api_key?: string;
  bearer_token?: string;
  client_id?: string;
  client_secret?: string;
  oauth2_token_url?: string;
  additional_headers?: string;
  is_active: boolean | number;
  created_at: string;
  updated_at: string;
}

export interface ServiceParam {
  id: number;
  service_id: number;
  param_name: string;
  param_value?: string;
  param_type: 'string' | 'number' | 'boolean' | 'json';
  description?: string;
  is_required: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface CreateServiceRequest {
  tenant_code: string;
  service_name: string;
  service_url: string;
  auth_type: 'basic' | 'bearer' | 'api_key' | 'oauth2';
  username?: string;
  password?: string;
  api_key?: string;
  bearer_token?: string;
  client_id?: string;
  client_secret?: string;
  oauth2_token_url?: string;
  additional_headers?: Record<string, string>;
  is_active?: boolean;
}

export interface UpdateServiceRequest {
  service_name?: string;
  service_url?: string;
  auth_type?: 'basic' | 'bearer' | 'api_key' | 'oauth2';
  username?: string;
  password?: string;
  api_key?: string;
  bearer_token?: string;
  client_id?: string;
  client_secret?: string;
  oauth2_token_url?: string;
  additional_headers?: Record<string, string>;
  is_active?: boolean;
}

export interface CreateServiceParamRequest {
  service_id: number;
  param_name: string;
  param_value?: string;
  param_type?: 'string' | 'number' | 'boolean' | 'json';
  description?: string;
  is_required?: boolean;
  sort_order?: number;
}

export interface UpdateServiceParamRequest {
  param_name?: string;
  param_value?: string;
  param_type?: 'string' | 'number' | 'boolean' | 'json';
  description?: string;
  is_required?: boolean;
  sort_order?: number;
}

class ServiceServiceClass {
  /**
   * 获取所有服务
   */
  async getServices(): Promise<{ services: Service[]; total: number }> {
    try {
      const response = await api.get(`${getApiBase()}/services`);
      // 后端返回的数据结构是 { success: true, data: [...], total: 2 }
      // 需要转换为前端期望的 { services: [...], total: 2 }
      return {
        services: response.data.data || [],
        total: response.data.total || 0
      };
    } catch (error: any) {
      console.error('获取服务列表失败:', error);
      throw new Error(error.response?.data?.message || '获取服务列表失败');
    }
  }

  /**
   * 根据ID获取服务
   */
  async getService(id: number): Promise<Service> {
    try {
      const response = await api.get(`${getApiBase()}/services/${id}`);
      return response.data.data;
    } catch (error: any) {
      console.error('获取服务详情失败:', error);
      throw new Error(error.response?.data?.message || '获取服务详情失败');
    }
  }

  /**
   * 创建服务
   */
  async createService(data: CreateServiceRequest): Promise<any> {
    try {
      const response = await api.post(`${getApiBase()}/services`, data);
      return response.data;
    } catch (error: any) {
      console.error('创建服务失败:', error);
      throw new Error(error.response?.data?.message || '创建服务失败');
    }
  }

  /**
   * 更新服务
   */
  async updateService(id: number, data: UpdateServiceRequest): Promise<any> {
    try {
      const response = await api.put(`${getApiBase()}/services/${id}`, data);
      return response.data;
    } catch (error: any) {
      console.error('更新服务失败:', error);
      throw new Error(error.response?.data?.message || '更新服务失败');
    }
  }

  /**
   * 删除服务
   */
  async deleteService(id: number): Promise<any> {
    try {
      const response = await api.delete(`${getApiBase()}/services/${id}`);
      return response.data;
    } catch (error: any) {
      console.error('删除服务失败:', error);
      throw new Error(error.response?.data?.message || '删除服务失败');
    }
  }

  /**
   * 获取服务的参数列表
   */
  async getServiceParams(serviceId: number): Promise<ServiceParam[]> {
    try {
      const response = await api.get(`${getApiBase()}/services/${serviceId}/params`);
      return response.data.data;
    } catch (error: any) {
      console.error('获取服务参数失败:', error);
      throw new Error(error.response?.data?.message || '获取服务参数失败');
    }
  }

  /**
   * 根据ID获取服务参数
   */
  async getServiceParam(serviceId: number, paramId: number): Promise<ServiceParam> {
    try {
      const response = await api.get(`${getApiBase()}/services/${serviceId}/params/${paramId}`);
      return response.data.data;
    } catch (error: any) {
      console.error('获取服务参数详情失败:', error);
      throw new Error(error.response?.data?.message || '获取服务参数详情失败');
    }
  }

  /**
   * 创建服务参数
   */
  async createServiceParam(serviceId: number, data: CreateServiceParamRequest): Promise<any> {
    try {
      const response = await api.post(`${getApiBase()}/services/${serviceId}/params`, data);
      return response.data;
    } catch (error: any) {
      console.error('创建服务参数失败:', error);
      throw new Error(error.response?.data?.message || '创建服务参数失败');
    }
  }

  /**
   * 更新服务参数
   */
  async updateServiceParam(serviceId: number, paramId: number, data: UpdateServiceParamRequest): Promise<any> {
    try {
      const response = await api.put(`${getApiBase()}/services/${serviceId}/params/${paramId}`, data);
      return response.data;
    } catch (error: any) {
      console.error('更新服务参数失败:', error);
      throw new Error(error.response?.data?.message || '更新服务参数失败');
    }
  }

  /**
   * 删除服务参数
   */
  async deleteServiceParam(serviceId: number, paramId: number): Promise<any> {
    try {
      const response = await api.delete(`${getApiBase()}/services/${serviceId}/params/${paramId}`);
      return response.data;
    } catch (error: any) {
      console.error('删除服务参数失败:', error);
      throw new Error(error.response?.data?.message || '删除服务参数失败');
    }
  }

  /**
   * 解析URL获取主机和端口
   */
  parseServiceUrl(url: string): { host: string; port: string; protocol: string } {
    try {
      const urlObj = new URL(url);
      return {
        host: urlObj.hostname,
        port: urlObj.port || (urlObj.protocol === 'https:' ? '443' : '80'),
        protocol: urlObj.protocol.replace(':', '')
      };
    } catch (error) {
      return {
        host: '',
        port: '',
        protocol: 'http'
      };
    }
  }

  /**
   * 构建服务URL
   */
  buildServiceUrl(protocol: string, host: string, port: string): string {
    const cleanProtocol = protocol.replace(':', '');
    const cleanPort = port && port !== '80' && port !== '443' ? `:${port}` : '';
    return `${cleanProtocol}://${host}${cleanPort}`;
  }
}

export const serviceService = new ServiceServiceClass();
export default serviceService;
